import { useEffect, useId, useRef, useState, type ReactNode } from 'react'


import type { CustomerRow } from '@/api/customers'

import { useReveals, type RevealState } from './customerReveal'
import { failureMessage } from './errors'

/** Why a reveal failed, honestly: nothing was revealed, and what to do about it. */
export function revealError(error: unknown): string {
  return failureMessage(
    error,
    {
      403: 'Not revealed: your role cannot reveal customer ids here, or this customer’s website is suspended.',
      404: 'Not revealed: this customer is no longer in your scope.',
    },
    'Not revealed: the id could not be fetched just now. Try again in a moment.',
  )
}

/**
 * A customer's identity cell: the masked reference as served, the full website customer
 * id once revealed on this page (until the page is left, or hidden), and the reveal
 * itself for a role that may reveal and a customer whose record has synced. Its one
 * control (Hide once revealed, else the reveal) is `control`, where focus comes back.
 */
export function IdentityCell({
  row,
  canReveal,
  state,
  control,
  onAsk,
  onHide,
}: {
  row: CustomerRow
  canReveal: boolean
  state: RevealState | undefined
  control: (button: HTMLButtonElement | null) => void
  onAsk: (row: CustomerRow) => void
  onHide: (customerId: string) => void
}) {
  const { customerId } = row
  return (
    <span className="list-cell-stack cus-identity">
      <span className="cus-ref">{row.customerRef}</span>
      {state?.status === 'revealed' && customerId ? (
        <span className="cus-revealed">
          <span>
            Website id <strong data-testid="revealed-id">{state.reveal.externalCustomerId}</strong>
          </span>
          <button ref={control} type="button" className="ci-button cus-inline-button" data-variant="ghost" onClick={() => onHide(customerId)}>
            Hide
          </button>
        </span>
      ) : null}
      {state?.status === 'loading' ? (
        <span role="status" className="list-cell-sub">
          Revealing…
        </span>
      ) : null}
      {state?.status === 'failed' ? (
        <span role="alert" className="list-error cus-reveal-error">
          {revealError(state.error)}
        </span>
      ) : null}
      {row.synced ? null : <span className="list-cell-sub">Not synced yet</span>}
      {canReveal && customerId && state?.status !== 'revealed' && state?.status !== 'loading' ? (
        <button ref={control} type="button" className="ci-button cus-inline-button" data-variant="ghost" onClick={() => onAsk(row)}>
          Reveal id<span className="ci-visually-hidden"> of customer {row.customerRef}</span>
        </button>
      ) : null}
    </span>
  )
}

/**
 * The question before a reveal: what it shows, for how long, and that it is audited.
 * Focus moves to it when it opens and Tab stays within it; Escape or Cancel closes it
 * without revealing.
 */
export function RevealConfirm({
  row,
  onConfirm,
  onCancel,
}: {
  row: CustomerRow
  onConfirm: () => void
  onCancel: () => void
}) {
  const titleId = useId()
  const bodyId = useId()
  const confirm = useRef<HTMLButtonElement>(null)
  const cancel = useRef<HTMLButtonElement>(null)
  useEffect(() => confirm.current?.focus(), [])
  return (
    <div
      role="alertdialog"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      className="cus-confirm"
      onKeyDown={(event) => {
        if (event.key === 'Escape') onCancel()
        if (event.key !== 'Tab') return
        // Two controls: Tab past either end wraps to the other.
        const [first, last] = [confirm.current, cancel.current]
        if (event.shiftKey ? document.activeElement === first : document.activeElement === last) {
          event.preventDefault()
          ;(event.shiftKey ? last : first)?.focus()
        }
      }}
    >
      <p id={titleId} className="cus-confirm__title">
        Reveal the website customer id of {row.customerRef}?
      </p>
      <p id={bodyId} className="list-note">
        This is recorded in the audit log, with your name, every time. The id shows on this page until you leave it, and
        is never saved or exported.
      </p>
      <div className="cus-confirm__actions">
        <button ref={confirm} type="button" className="ci-button" data-variant="primary" onClick={onConfirm}>
          Reveal id
        </button>
        <button ref={cancel} type="button" className="ci-button" data-variant="ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}

/**
 * The reveal, start to finish, for a list of customers: each row's identity cell, and
 * the question before a reveal (`confirm`, null while none is asked). It holds what was
 * asked and the ids revealed (`useReveals`: this page's state only), and hands focus
 * back: to the asking control on Cancel, and to the row's control (Hide, or the reveal
 * after a failure) once a confirmed reveal settles.
 */
export function useRevealFlow(canReveal: boolean): { identity: (row: CustomerRow) => ReactNode; confirm: ReactNode } {
  const { reveals, reveal, hide } = useReveals()
  const [asking, setAsking] = useState<CustomerRow | null>(null)
  const asker = useRef<HTMLElement | null>(null)
  // Each row's control, by customer, and the customer whose reveal is to take focus when it settles.
  const controls = useRef(new Map<string, HTMLButtonElement>())
  const settling = useRef<string | null>(null)

  useEffect(() => {
    const customerId = settling.current
    const status = customerId ? reveals.get(customerId)?.status : undefined
    if (!customerId || status === 'loading') return
    settling.current = null
    controls.current.get(customerId)?.focus()
  }, [reveals])

  const identity = (row: CustomerRow) => (
    <IdentityCell
      row={row}
      canReveal={canReveal}
      state={row.customerId ? reveals.get(row.customerId) : undefined}
      control={(button) => {
        if (!row.customerId) return
        if (button) controls.current.set(row.customerId, button)
        else controls.current.delete(row.customerId)
      }}
      onAsk={(asked) => {
        asker.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
        setAsking(asked)
      }}
      onHide={hide}
    />
  )

  const customerId = asking?.customerId
  const confirm =
    asking && customerId ? (
      <RevealConfirm
        row={asking}
        onConfirm={() => {
          settling.current = customerId
          reveal(customerId)
          setAsking(null)
        }}
        onCancel={() => {
          setAsking(null)
          asker.current?.focus()
        }}
      />
    ) : null

  return { identity, confirm }
}
